// ABOUTME: Main ShDataGrid3 component — the vLast replacement for ShDataGrid2 on non-sidepanel grids.
// ABOUTME: Orchestrates Redux reads, RTK fetch, state transitions; delegates desktop rendering to the memoized ShGridBody.

import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Box, useTheme } from "@mui/material";
import { useDispatch, useSelector, useStore } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import cx from "classnames";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { arrayMove } from "@dnd-kit/sortable";

import { DropdownBase } from "../dropdowns/Dropdown";

import { tokens } from "../../../theme";
import { useGridFilter } from "../../../contexts/GridFilterContext";
import {
    setShGridColumns,
    updateSort,
    updateFilter,
    updateGroupBy,
    updateRowHeight,
    clearFilterForInstance,
} from "../../../newapi/global/filter/gridFilterSlice";
import {
    shDatagridPageSizeOptions,
    SH_DATAGRID_DEFAULT_ROW_HEIGHT,
} from "../../../newapi/global/filter/DataGridGeneralOptions";

import Subheading from "../text/Subheading";
import GridFilter3 from "./GridFilter3";
import ShGridError from "../datagrid2/Status/ShGridError";
import ShGridEmpty from "../datagrid2/Status/ShGridEmpty";
import { PaginationDropUp } from "../datagrid2/PaginationDropUp";

import ShGridBody from "./ShGridBody";
import ShGridConfigDropdown from "./ShGridConfigDropdown";
import MSaveGridConfig from "./MSaveGridConfig";
import useGridLayout from "./useGridLayout";
import { usePutTableSettingsMutation } from "../../../newapi/global/tableSettings/tableSettingsSlice";
import { showToastMessageUpdateRequest, showToastMessageDeleteRequest } from "../../../utils/toasts";
import { resolveDateGranularities } from "./groupingDateGranularities";
import { resolveSelectedRows, toggleAllSelection } from "./selection";
import columnsToGridTemplate, {
    computePinnedOffsets,
} from "./columnsToGridTemplate";

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

const MOBILE_BREAKPOINT_PX = 767;
const SKELETON_ROW_COUNT = 7;
const SKELETON_DELAY_MS = 500;
const INFINITE_SCROLL_PAGE_SIZE = 20;
const DEFAULT_POLLING_INTERVAL_MS = 30000;

const generateSkeletonRows = () =>
    Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => ({
        id: `skeleton-${i}`,
        skeleton: true,
    }));

// ============================================================================
// Component
// ============================================================================

const ShDataGrid3 = (props) => {
    // ------------------------------------------------------------------------
    // Props validation
    // ------------------------------------------------------------------------

    if (props.selectable && !props.setSelected && !props.setSelectedRows) {
        throw new Error("ShDataGrid3: `selectable` requires `setSelected` or `setSelectedRows`.");
    }
    if (!props.gridKey && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("ShDataGrid3: `gridKey` prop is required. Saved configs will not work.");
    }

    // ------------------------------------------------------------------------
    // Context + Redux reads
    // ------------------------------------------------------------------------

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { index, id } = useGridFilter();
    const [putTableSettings] = usePutTableSettingsMutation();
    const store = useStore();

    const runtimeInstance = useSelector((state) => {
        return state.gridFilter?.viewInstances?.[index]?.[id] || null;
    });
    const runtimeColumns = runtimeInstance?.columns;
    const runtimeSortModel = runtimeInstance?.sortModel;
    const runtimeFilter = runtimeInstance?.filter;
    const runtimeSearchString = runtimeInstance?.searchString || "";
    const runtimeGroupBy = runtimeInstance?.groupBy ?? null;

    const pageSize = useSelector(
        (state) => state.gridFilter?.settings?.pageSize3 ?? shDatagridPageSizeOptions[0]
    );

    const rowHeight = useSelector(
        (state) => state.gridFilter?.settings?.rowHeight ?? SH_DATAGRID_DEFAULT_ROW_HEIGHT
    );

    // Push the entire userLayout document to the backend and show a toast.
    // Reads fresh state via store.getState() so it captures changes from
    // dispatches that happened earlier in the same call (before React re-renders).
    const syncToBackend = useCallback(
        async () => {
            const doc = store.getState().userLayout?.document;
            try {
                const response = await putTableSettings({
                    columnConfigs: doc?.columnConfigs ?? {},
                    generalSettings: doc?.generalSettings ?? {},
                    tabConfigs: doc?.tabConfigs ?? {},
                }).unwrap();
                showToastMessageUpdateRequest(t, response);
            } catch (err) {
                showToastMessageUpdateRequest(t, { success: false, error: err?.data?.error ?? "unknown" });
            }
        },
        [putTableSettings, store, t]
    );

    // Sidebar collapsed state — drives the fixed-header width math so the fixed
    // title bar aligns with the visible content area.
    const collapsed = useSelector((state) => state.settings?.environment?.collapsed ?? false);

    // ------------------------------------------------------------------------
    // fixedHeader — title bar becomes position:fixed when the page scrolls past it
    // ------------------------------------------------------------------------

    const refContainer = useRef(null);
    const [fixedHeader, setFixedHeader] = useState(false);

    useEffect(() => {
        const checkScrollPosition = () => {
            if (!refContainer.current) return;
            const topPosition = refContainer.current.getBoundingClientRect().top - 72;
            setFixedHeader(topPosition <= 0);
        };

        window.addEventListener("scroll", checkScrollPosition);
        checkScrollPosition();
        return () => {
            window.removeEventListener("scroll", checkScrollPosition);
        };
    }, []);

    // ------------------------------------------------------------------------
    // Saved configs + mount-flow handoff
    // ------------------------------------------------------------------------

    const layout = useGridLayout(props.gridKey, props.family, props.columns);
    const hasAppliedRef = useRef(false);

    // Normalize `props.family` (array of gridKey strings, array of {gridKey,label},
    // or object keyed by alias → gridKey string or {gridKey,label}) into a flat list
    // of sibling members `{gridKey, label}` excluding self.
    const familyMembers = useMemo(() => {
        const raw = Array.isArray(props.family)
            ? props.family
            : props.family && typeof props.family === "object"
                ? Object.values(props.family)
                : [];
        return raw
            .map((entry) =>
                typeof entry === "string"
                    ? { gridKey: entry, label: entry }
                    : entry && typeof entry === "object" && entry.gridKey
                        ? { gridKey: entry.gridKey, label: entry.label ?? entry.gridKey }
                        : null
            )
            .filter((m) => m && m.gridKey !== props.gridKey);
    }, [props.family, props.gridKey]);

    useEffect(() => {
        if (!runtimeInstance) return;
        if (hasAppliedRef.current) return;
        layout.actions.applySavedConfig();
        hasAppliedRef.current = true;

        // Apply prop-level defaults after the saved config has been applied.
        // Saved config values take priority — these only fill in what's missing.
        if (props.defaultGroupBy?.field && !layout.activeConfig?.groupBy?.field) {
            dispatch(updateGroupBy({ index, id, groupBy: props.defaultGroupBy }));
        }
        if (props.defaultRowHeight != null && layout.activeConfig?.rowHeight == null) {
            dispatch(updateRowHeight(props.defaultRowHeight));
        }
        if (props.sortModel?.field && !layout.activeConfig?.sortModel?.field) {
            dispatch(updateSort({ index, id, sortModel: props.sortModel }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runtimeInstance]);

    useEffect(() => {
        hasAppliedRef.current = false;
    }, [index, id, props.gridKey]);

    // ------------------------------------------------------------------------
    // Config prop → gridFilterSlice sync
    // ------------------------------------------------------------------------

    const prevConfigRef = useRef(null);
    useEffect(() => {
        const cfg = props.config;
        if (!cfg) return;
        if (!runtimeInstance) return;
        if (JSON.stringify(prevConfigRef.current) === JSON.stringify(cfg)) return;
        prevConfigRef.current = cfg;

        // Only skip the props.config → gridFilter sync when a LOCALLY-OWNED config
        // with filters is active — the owner's saved filters should win over
        // props.config. For siblings viewing a shared (pointer-resolved) config,
        // the shared config's filters never get applied (see useGridLayout), so
        // props.config must still be synced to keep the tab's data query correct
        // (e.g. `.backordered` always needs `state: BACKORDERED`).
        if (
            layout.activeConfigId !== "default" &&
            layout.isActiveConfigLocal &&
            layout.activeConfig?.filters
        ) {
            return;
        }

        dispatch(clearFilterForInstance({ index, id }));
        Object.entries(cfg).forEach(([filterKey, value]) => {
            dispatch(updateFilter({ index, id, filterKey, value }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.config, runtimeInstance, index, id]);

    // ------------------------------------------------------------------------
    // Pagination (server-side, 1-based)
    // ------------------------------------------------------------------------

    const [activePage, setActivePage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    // Total row count for the active filter set, straight from the API response
    // (`totalItems` in the paging envelope). Shown in the pagination bar.
    const [totalItems, setTotalItems] = useState(0);

    const prevPage = useCallback(() => {
        setActivePage((p) => Math.max(1, p - 1));
    }, []);
    const nextPage = useCallback(() => {
        setActivePage((p) => Math.min(totalPages || 1, p + 1));
    }, [totalPages]);
    const selectPage = useCallback((page) => {
        setActivePage(Number(page));
    }, []);
    
    // DropdownBase's onChange passes the SELECTED INDEX of the options array, not the
    // value itself — so we resolve `shDatagridPageSizeOptions[selected]` before dispatching.
    // Same contract ShDataGrid2 uses (see datagrid2/ShDataGrid2.jsx:418).
    // Goes through useGridLayout.setPageSize which writes to both userLayoutSlice
    // (persistent) and gridFilterSlice (runtime), mirroring how columnConfigs work.
    const handlePageSizeUpdate = useCallback(
        (selected) => {
            layout.actions.setPageSize(shDatagridPageSizeOptions[selected]);
            setActivePage(1);
        },
        [layout]
    );

    // Infinite scroll accumulator — lives outside the normal rows/setRows flow.
    // Holds all rows fetched so far across pages. Reset when filters/sort change.
    const infiniteRowsRef = useRef([]);
    const infiniteLastPageRef = useRef(0);

    useEffect(() => {
        setActivePage(1);
        if (props.infiniteScroll) {
            infiniteRowsRef.current = [];
            infiniteLastPageRef.current = 0;
        }
    }, [runtimeSearchString, runtimeFilter, runtimeSortModel]);

    // paginationPages: the left-side clump of visible page numbers (same algorithm as ShDataGrid2)
    const paginationPages = useMemo(() => {
        const pages = [];
        if (activePage > 4) {
            for (let i = Math.max(1, activePage - 3); i <= Math.min(totalPages, activePage); i++) {
                pages.push(i);
            }
        } else {
            for (let i = 1; i <= Math.min(4, totalPages); i++) {
                pages.push(i);
            }
        }
        return pages;
    }, [activePage, totalPages]);

    // ------------------------------------------------------------------------
    // Data fetching via props.content
    // ------------------------------------------------------------------------

    const queryArgs = useMemo(() => {
        const body = {
            // Base config (state filter, relationId, etc.) is always present in
            // the query regardless of what gridFilterSlice holds. Runtime filters
            // are spread on top so user-applied filters can override base keys.
            ...(props.config || {}),
            ...(runtimeFilter || {}),
            searchString: runtimeSearchString || "",
            sort: runtimeSortModel || {},
        };
        return {
            ...(props.itemId != null && { id: props.itemId }),
            page: activePage,
            size: props.infiniteScroll ? INFINITE_SCROLL_PAGE_SIZE : pageSize,
            body,
        };
    }, [props.itemId, props.config, activePage, pageSize, props.infiniteScroll, runtimeFilter, runtimeSearchString, runtimeSortModel]);

    const effectiveSize = props.infiniteScroll ? INFINITE_SCROLL_PAGE_SIZE : pageSize;
    const contentResult = props.content
        ? props.content(
              { args: queryArgs },
              {
                  skip:
                      !effectiveSize ||
                      effectiveSize < 0 ||
                      !queryArgs.body ||
                      Object.keys(queryArgs.body).length === 0,
                  ...(props.pollingInterval && { pollingInterval: DEFAULT_POLLING_INTERVAL_MS }),
              }
          )
        : { data: null, isLoading: false, isFetching: false, isSuccess: false, isError: false };

    const {
        data: fetchedData,
        isFetching: fetchIsFetching,
        isSuccess: fetchIsSuccess,
        isError: fetchIsError,
    } = contentResult;

    const infiniteScrollLoadingRef = useRef(false);
    useEffect(() => {
        if (!fetchIsFetching) infiniteScrollLoadingRef.current = false;
    }, [fetchIsFetching]);

    const handleScrollEnd = useCallback(() => {
        if (!props.infiniteScroll) return;
        if (fetchIsFetching || infiniteScrollLoadingRef.current) return;
        if (activePage >= totalPages) return;
        infiniteScrollLoadingRef.current = true;
        setActivePage((p) => p + 1);
    }, [props.infiniteScroll, fetchIsFetching, activePage, totalPages]);

    const [rows, setRows] = useState(() => props.rows ?? []);
    const skeletonTimerRef = useRef(null);

    useEffect(() => {
        if (!props.content) {
            setRows(props.rows ?? []);
        }
    }, [props.rows, props.content]);

    useEffect(() => {
        if (!props.content) return;
        if (props.infiniteScroll && infiniteRowsRef.current.length > 0) return;
        if (fetchIsFetching && !fetchedData) {
            skeletonTimerRef.current = setTimeout(() => {
                setRows(generateSkeletonRows());
            }, SKELETON_DELAY_MS);
        }
        return () => {
            if (skeletonTimerRef.current) {
                clearTimeout(skeletonTimerRef.current);
                skeletonTimerRef.current = null;
            }
        };
    }, [fetchIsFetching, fetchedData, props.content]);

    useEffect(() => {
        if (!props.content) return;
        if (fetchIsSuccess && fetchedData) {
            if (skeletonTimerRef.current) {
                clearTimeout(skeletonTimerRef.current);
                skeletonTimerRef.current = null;
            }
            const rowsFromFetch = fetchedData.data || [];

            if (props.infiniteScroll) {
                if (activePage > infiniteLastPageRef.current) {
                    // New page arrived — append to accumulator
                    infiniteRowsRef.current = [...infiniteRowsRef.current, ...rowsFromFetch];
                    infiniteLastPageRef.current = activePage;
                } else if (activePage === 1) {
                    // Reset (filter/sort changed)
                    infiniteRowsRef.current = rowsFromFetch;
                    infiniteLastPageRef.current = 1;
                }
                setRows(infiniteRowsRef.current);
            } else {
                setRows(rowsFromFetch);
            }

            setTotalPages(fetchedData.totalPages ?? 0);
            setTotalItems(fetchedData.totalItems ?? 0);
            if (props.onDataChange) props.onDataChange(rowsFromFetch);
            if (props.onEmpty) props.onEmpty(rowsFromFetch.length === 0);
        }
    }, [fetchIsSuccess, fetchedData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!props.content) return;
        if (fetchIsError) {
            if (props.infiniteScroll) {
                infiniteRowsRef.current = [];
                infiniteLastPageRef.current = 0;
            }
            setRows([]);
            setTotalPages(0);
            setTotalItems(0);
            if (props.onDataChange) props.onDataChange([]);
        }
    }, [fetchIsError]); // eslint-disable-line react-hooks/exhaustive-deps

    const actualData = useMemo(() => {
        if (rows?.length > 0) return rows;
        if (fetchIsSuccess && fetchedData?.data?.length > 0) return fetchedData.data;
        return null;
    }, [rows, fetchIsSuccess, fetchedData]);

    // ------------------------------------------------------------------------
    // Row grouping
    // ------------------------------------------------------------------------
    //
    // Runtime `groupBy` lives in gridFilterSlice and is saved/loaded through
    // userLayoutSlice (see useGridLayout.applySavedConfig + buildConfigFromRuntime).
    // At render time we:
    //   1. Look up the column the runtime groupBy references
    //   2. If that column has a `grouping` block, use its bucket function to bucket
    //      rows by key (respecting the optional granularity); otherwise schema-drift
    //      guard kicks in and we render flat.
    //   3. Interleave synthetic `__isGroupHeader` rows between buckets. ShGridBody
    //      renders them via ShGridGroupHeader instead of ShGridRow.
    //
    // Collapse state is ephemeral (component-local). Switching the groupBy clears it.

    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

    // Reset collapse state whenever groupBy changes — old keys are meaningless for
    // a new field/granularity.
    const groupBySignature = runtimeGroupBy
        ? `${runtimeGroupBy.field}::${runtimeGroupBy.granularity ?? ""}`
        : "";
    const prevGroupBySigRef = useRef(groupBySignature);
    useEffect(() => {
        if (prevGroupBySigRef.current !== groupBySignature) {
            prevGroupBySigRef.current = groupBySignature;
            setCollapsedGroups(new Set());
        }
    }, [groupBySignature]);

    const handleToggleGroup = useCallback((groupKey) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    }, []);

    // Resolve the column + (optional) granularity referenced by the runtime
    // groupBy. Returns null if grouping is off, disabled by the grid, or the
    // saved field no longer has a grouping block (schema drift → silent fallback
    // to flat rendering).
    //
    // `toKey(value)` is the function we use to turn a row's raw value into the
    // group key. For date columns (columns whose grouping block has
    // `granularities`) the selected granularity's built-in rounding helper does
    // the work. For every other column we pass the raw value through unchanged.
    const groupingResolution = useMemo(() => {
        if (props.enableGrouping !== true) return null;
        if (!runtimeGroupBy?.field) return null;
        if (!Array.isArray(props.columns)) return null;

        const column = props.columns.find((c) => c.field === runtimeGroupBy.field);
        if (!column?.grouping) return null;

        const grouping = column.grouping;
        let toKey = grouping.toKey || ((value) => value);
        let renderLabel = grouping.renderLabel;
        let chosenGranularity = null;

        if (Array.isArray(grouping.granularities) && grouping.granularities.length > 0) {
            const granularities = resolveDateGranularities(grouping.granularities);
            const chosen =
                granularities.find((g) => g.id === runtimeGroupBy.granularity) ??
                granularities[0];
            if (!chosen) return null;
            chosenGranularity = chosen;
            toKey = (value) => chosen.bucket(value);
            if (!renderLabel) {
                renderLabel = (key) => chosen.formatKey(key, i18n.language);
            }
        }

        return { column, grouping, toKey, renderLabel, granularity: chosenGranularity };
    }, [props.enableGrouping, props.columns, runtimeGroupBy, i18n.language]);

    const displayRows = useMemo(() => {
        const rawRows = actualData || [];
        if (!groupingResolution || rawRows.length === 0) return rawRows;

        const { toKey, renderLabel, column, granularity } = groupingResolution;
        const field = column.field;

        const order = [];
        const groups = new Map();

        for (const row of rawRows) {
            const key = toKey(row?.[field]);
            if (!groups.has(key)) {
                groups.set(key, []);
                order.push(key);
            }
            groups.get(key).push(row);
        }

        const result = [];
        for (const key of order) {
            const rowsInGroup = groups.get(key);
            const isCollapsed = collapsedGroups.has(key);
            const label = renderLabel
                ? renderLabel(key, rowsInGroup.length, granularity)
                : String(key);
            result.push({
                __isGroupHeader: true,
                id: `__group__${String(key)}`,
                __groupKey: key,
                __groupLabel: label,
                __groupCount: rowsInGroup.length,
                __collapsed: isCollapsed,
            });
            if (!isCollapsed) {
                for (const r of rowsInGroup) result.push(r);
            }
        }
        return result;
    }, [actualData, groupingResolution, collapsedGroups]);

    // ------------------------------------------------------------------------
    // Selection state
    // ------------------------------------------------------------------------

    const [selectedItems, setSelectedItems] = useState(props.selectedItems ?? []);

    useEffect(() => {
        setSelectedItems(props.selectedItems ?? []);
    }, [props.selectedItems]);

    // Full rows of the selection, so rows ticked on an earlier page stay in setSelectedRows.
    const selectedRowsRef = useRef(new Map());

    const notifySelectionChange = useCallback(
        (newSelection) => {
            if (props.setSelectedRows) {
                selectedRowsRef.current = resolveSelectedRows(newSelection, actualData, selectedRowsRef.current);
                props.setSelectedRows([...selectedRowsRef.current.values()]);
            } else if (props.setSelected) {
                props.setSelected(newSelection);
            }
        },
        [props.setSelected, props.setSelectedRows, actualData]
    );

    const handleToggleRow = useCallback(
        (rowId) => {
            setSelectedItems((prev) => {
                const next = prev.includes(rowId)
                    ? prev.filter((id) => id !== rowId)
                    : [...prev, rowId];
                notifySelectionChange(next);
                return next;
            });
        },
        [notifySelectionChange]
    );

    const handleToggleAll = useCallback(() => {
        const selectableRows = (actualData || []).filter((r) => {
            if (r.skeleton || r.__isGroupHeader) return false;
            if (props.isRowSelectable && !props.isRowSelectable(r)) return false;
            return true;
        });
        const selectableIds = selectableRows.map((r) => r.id);
        const next = toggleAllSelection(selectedItems, selectableIds, !!props.keepSelectionAcrossPages);
        setSelectedItems(next);
        notifySelectionChange(next);
    }, [actualData, selectedItems, props.isRowSelectable, props.keepSelectionAcrossPages, notifySelectionChange]);

    const handleRowClick = useCallback(
        (params) => {
            if (props.onRowClick) props.onRowClick(params);
        },
        [props.onRowClick]
    );

    // ------------------------------------------------------------------------
    // Mobile detection
    // ------------------------------------------------------------------------

    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT_PX);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT_PX);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // ------------------------------------------------------------------------
    // Column resolution for rendering
    // ------------------------------------------------------------------------

    const codeColumnsByField = useMemo(() => {
        const map = new Map();
        if (Array.isArray(props.columns)) {
            for (const col of props.columns) {
                map.set(col.field, col);
            }
        }
        return map;
    }, [props.columns]);

    // Visible columns sorted into pin zones: [...leftPinned, ...unpinned, ...rightPinned].
    // This physical reorder is what makes a pinned column actually appear at the edge of
    // the grid at rest — `position: sticky` alone keeps the cell in its natural track and
    // only "sticks" once the user scrolls past it, which is not what users mean by pinning.
    // All downstream derivations (resolvedColumns, gridTemplate, pinnedOffsets,
    // visibleFieldsForSortable) consume this sorted order, so enforcing zones here
    // propagates through the whole rendering pipeline.
    const visibleRuntime = useMemo(() => {
        if (!Array.isArray(runtimeColumns)) return [];
        const visible = runtimeColumns.filter((rc) => {
            if (rc.visible === false) return false;
            if (rc.column === "phoneView") return false;
            return true;
        });
        const leftPinned = visible.filter((rc) => rc.pinned === "left");
        const rightPinned = visible.filter((rc) => rc.pinned === "right");
        const unpinned = visible.filter(
            (rc) => rc.pinned !== "left" && rc.pinned !== "right"
        );
        return [...leftPinned, ...unpinned, ...rightPinned];
    }, [runtimeColumns]);

    const resolvedColumns = useMemo(() => {
        return visibleRuntime
            .map((rc) => {
                const codeCol = codeColumnsByField.get(rc.column);
                if (!codeCol) return null;
                return { ...codeCol, pinned: rc.pinned ?? null };
            })
            .filter(Boolean);
    }, [visibleRuntime, codeColumnsByField]);

    // NOTE: withAddColumn intentionally OMITTED — the + add-column affordance is an
    // absolute hover overlay, not a grid column, so it must not be in the template.
    const gridTemplate = useMemo(
        () => columnsToGridTemplate(resolvedColumns),
        [resolvedColumns]
    );

    const pinnedOffsets = useMemo(() => computePinnedOffsets(resolvedColumns), [resolvedColumns]);

    const visibleFieldsForSortable = useMemo(
        () => visibleRuntime.map((rc) => rc.column),
        [visibleRuntime]
    );

    // ------------------------------------------------------------------------
    // Hidden columns for the + popover
    // ------------------------------------------------------------------------

    const hiddenColumns = useMemo(() => {
        if (!Array.isArray(props.columns)) return [];
        const visibleSet = new Set(visibleRuntime.map((rc) => rc.column));
        return props.columns.filter((c) => !visibleSet.has(c.field) && c.field !== "phoneView");
    }, [props.columns, visibleRuntime]);

    const handleAddColumn = useCallback(
        (field) => {
            if (!Array.isArray(runtimeColumns)) return;
            const nextColumns = [
                ...runtimeColumns.filter((rc) => rc.column !== field),
                { column: field, visible: true, pinned: null },
            ];
            dispatch(setShGridColumns({ index, id, columns: nextColumns }));
        },
        [dispatch, index, id, runtimeColumns]
    );

    // ------------------------------------------------------------------------
    // Drag-end handler
    // ------------------------------------------------------------------------

    const handleDragEnd = useCallback(
        (event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            if (!Array.isArray(runtimeColumns)) return;

            const visibleFields = visibleRuntime.map((rc) => rc.column);
            const oldIdx = visibleFields.indexOf(active.id);
            const newIdx = visibleFields.indexOf(over.id);
            if (oldIdx < 0 || newIdx < 0) return;

            const reorderedVisible = arrayMove(visibleFields, oldIdx, newIdx);
            const visibleRuntimeByField = new Map(visibleRuntime.map((rc) => [rc.column, rc]));
            const nextVisible = reorderedVisible.map((f) => visibleRuntimeByField.get(f));
            const hiddenRuntime = runtimeColumns.filter((rc) => rc.visible === false);
            const nextColumns = [...nextVisible, ...hiddenRuntime];

            dispatch(setShGridColumns({ index, id, columns: nextColumns }));
        },
        [dispatch, index, id, runtimeColumns, visibleRuntime]
    );

    // ------------------------------------------------------------------------
    // Mobile phoneView column
    // ------------------------------------------------------------------------

    const phoneViewColumn = useMemo(
        () => (Array.isArray(props.columns) ? props.columns.find((c) => c.field === "phoneView") : null),
        [props.columns]
    );

    // ------------------------------------------------------------------------
    // Save-view modal
    // ------------------------------------------------------------------------

    const [saveAsNewOpen, setSaveAsNewOpen] = useState(false);
    const [updateViewOpen, setUpdateViewOpen] = useState(false);

    const handleSaveAsNew = useCallback(
        (options) => {
            // If the user was viewing a shared (pointer) config when they hit Save,
            // creating a local copy implicitly severs the link. saveAs already sets
            // the new configId active — pass skipRuntimeReset so detach doesn't wipe it.
            const sharedConfigId = !layout.isActiveConfigLocal && layout.activeConfig
                ? layout.activeConfig.id
                : null;
            layout.actions.saveAs(options);
            if (sharedConfigId) {
                layout.actions.detachSharedConfig(sharedConfigId, { skipRuntimeReset: true });
            }
            syncToBackend();
        },
        [layout.actions, layout.activeConfig, layout.isActiveConfigLocal, syncToBackend]
    );

    const handleUpdateView = useCallback(
        (options) => {
            layout.actions.saveCurrent(options);
            syncToBackend();
        },
        [layout.actions, syncToBackend]
    );

    // ------------------------------------------------------------------------
    // hideWhenEmpty short-circuit
    // ------------------------------------------------------------------------

    if (props.hideWhenEmpty && fetchIsSuccess && fetchedData?.data?.length === 0) {
        return null;
    }

    const hideTitleBar = props.gridOptions?.hide === true || props.infiniteScroll === true;
    const dataRows = actualData || [];

    // ------------------------------------------------------------------------
    // Shared pill-button sx for pagination page buttons (matches ShDataGrid2)
    // ------------------------------------------------------------------------

    const pillBtnSx = {
        height: 40,
        minWidth: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 12,
        color: colors.txt.primary,
        position: "relative",
        cursor: "pointer",
        userSelect: "none",
        transition: "250ms",
        border: "none",
        background: "transparent",
        px: 1,
        fontSize: 16,
        fontFamily: "Roboto, sans-serif",
        "&.active": {
            background:
                theme.palette.mode === "dark"
                    ? (colors.pastel?.light?.blue?.[100] ?? colors.blue[100])
                    : colors.primary[100],
            color: theme.palette.mode === "dark" ? "#000" : null,
            fontWeight: 700,
        },
        "&:hover": {
            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
            transition: "250ms",
        },
        "&.active:hover": {
            background:
                theme.palette.mode === "dark"
                    ? (colors.pastel?.light?.blue?.[100] ?? colors.blue[100])
                    : colors.primary[100],
            color: theme.palette.mode === "dark" ? "#000" : null,
            fontWeight: 700,
        },
        "@media screen and (max-width: 47.9375em)": {
            width: 48,
            height: 48,
        },
    };


    // ------------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------------

    return (
        <Box
            ref={refContainer}
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                position: "relative",
            }}
        >
            {/* -------------------- TITLE BAR -------------------- */}
            {!hideTitleBar && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingX: 3,
                        paddingY: 3,
                        pb:2,
                        backgroundColor:
                            theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        flexShrink: 0,
                        "@media screen and (max-width: 47.9375em)": {
                            display: "none",
                        },
                        ...(fixedHeader && {
                            position: "fixed",
                            width: collapsed ? 1 : "calc(100% - 342px)",
                            left: collapsed ? 0 : 342,
                            top: 72,
                            background:
                                theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            zIndex: 300,
                            borderTopLeftRadius: 24,
                            borderTopRightRadius: 24,
                            transition: "left 500ms",
                            "&:before": {
                                content: "''",
                                display: "block",
                                width: "24px",
                                height: "48px",
                                position: "absolute",
                                left: 0,
                                top: 0,
                                zIndex: 1,
                                backgroundColor:
                                    theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                borderTopLeftRadius: "24px",
                                boxShadow: `0 -24px 0 0 ${colors.bg.tertiary}`,
                            },
                            "&:after": {
                                content: "''",
                                display: "block",
                                width: "24px",
                                height: "48px",
                                position: "absolute",
                                top: 0,
                                right: 0,
                                zIndex: 1,
                                backgroundColor:
                                    theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                borderTopLeftRadius: "24px",
                                boxShadow: `0 -24px 0 0 ${colors.bg.tertiary}`,
                                transform: "scaleX(-1)",
                            },
                            "@media screen and (max-width: 85.375em)": {
                                width: 1,
                                left: 0,
                            },
                        }),
                    }}
                >
                    <ShGridConfigDropdown
                        title={props.title}
                        explainer={
                            typeof props.explainer !== "undefined" && props.explainer !== null
                                ? props.explainer
                                : null
                        }
                        configs={layout.configs}
                        activeConfigId={layout.activeConfigId}
                        defaultConfigId={layout.defaultConfigId}
                        onSelectConfig={layout.actions.setActiveConfig}
                        onRenameConfig={(...args) => { layout.actions.renameConfig(...args); syncToBackend(); }}
                        onDeleteConfig={(...args) => { layout.actions.deleteConfig(...args); syncToBackend(); }}
                        onDetachConfig={(...args) => { layout.actions.detachSharedConfig(...args); syncToBackend(); }}
                        onSetDefaultConfig={(configId) => {
                            layout.actions.setDefaultConfig(configId);
                            layout.actions.setActiveConfig(configId);
                            syncToBackend();
                        }}
                    />
                    {props.gridOptions?.hide === undefined || !props.gridOptions?.hide ? (
                        <GridFilter3
                            disableViewSaving={props.disableViewSaving}
                            gridOptions={props.gridOptions}
                            gridActions={props.gridActions}
                            columns={props.columns}
                            onSaveAsNew={() => setSaveAsNewOpen(true)}
                            onUpdateView={() => setUpdateViewOpen(true)}
                            hasActiveConfig={
                                layout.activeConfigId !== "default" &&
                                !!layout.activeConfig &&
                                layout.isActiveConfigLocal
                            }
                        />
                    ) : null}
                </Box>
            )}

            {/* -------------------- MAIN CONTENT AREA -------------------- */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    position: "relative",
                    margin: props.inline ? null : "0 24px 0 24px",
                    paddingBottom: props.infiniteScroll ? 0 : 16,
                    ...(fixedHeader && { paddingTop: "112px" }),
                    "@media screen and (max-width: 47.937em)": {
                        margin: "0 !important",
                        pb:0,
                    },
                }}
            >
                <AnimatePresence mode="wait">
                    {!isMobile ? (
                        fetchIsError ? (
                            <motion.div
                                key="desktop-error"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2, ease: "easeInOut" }}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ShGridError />
                            </motion.div>
                        ) : actualData && actualData.length > 0 ? (
                            <motion.div
                                key={props.infiniteScroll ? "desktop-data-infinite" : `desktop-data-${JSON.stringify(queryArgs)}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15, ease: "easeInOut" }}
                                style={{ height: "100%", width: "100%" }}
                            >
                                <ShGridBody
                                    dataRows={displayRows}
                                    visibleRuntime={visibleRuntime}
                                    resolvedColumns={resolvedColumns}
                                    codeColumnsByField={codeColumnsByField}
                                    pinnedOffsets={pinnedOffsets}
                                    gridTemplate={gridTemplate}
                                    hiddenColumns={hiddenColumns}
                                    newColumnFields={layout.isActiveConfigLocal ? layout.newColumnFields : []}
                                    selectedItems={selectedItems}
                                    visibleFieldsForSortable={visibleFieldsForSortable}
                                    onRowClick={handleRowClick}
                                    onAddColumn={handleAddColumn}
                                    onAcknowledgeNewColumns={layout.actions.acknowledgeNewColumns}
                                    onDragEnd={handleDragEnd}
                                    fixedHeader={props.infiniteScroll ? false : fixedHeader}
                                    collapsed={collapsed}
                                    rowHeight={rowHeight}
                                    onToggleGroup={props.infiniteScroll ? undefined : handleToggleGroup}
                                    onScrollEnd={handleScrollEnd}
                                    overscrollContain={!!props.infiniteScroll}
                                    selectable={!!props.selectable}
                                    onToggleRow={handleToggleRow}
                                    onToggleAll={handleToggleAll}
                                    isRowSelectable={props.isRowSelectable}
                                    getRowClassName={props.getRowClassName}
                                    getRowTooltip={props.getRowTooltip}
                                    infiniteScroll={props.infiniteScroll}
                                />
                            </motion.div>
                        ) : fetchIsSuccess && fetchedData?.data?.length === 0 ? (
                            <motion.div
                                key="desktop-empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2, ease: "easeInOut" }}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ShGridEmpty />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="desktop-loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2, ease: "easeInOut" }}
                                style={{ height: "100%", width: "100%"}}
                            >
                                <ShGridBody
                                    dataRows={displayRows}
                                    visibleRuntime={visibleRuntime}
                                    resolvedColumns={resolvedColumns}
                                    codeColumnsByField={codeColumnsByField}
                                    pinnedOffsets={pinnedOffsets}
                                    gridTemplate={gridTemplate}
                                    hiddenColumns={hiddenColumns}
                                    newColumnFields={layout.isActiveConfigLocal ? layout.newColumnFields : []}
                                    selectedItems={selectedItems}
                                    visibleFieldsForSortable={visibleFieldsForSortable}
                                    onRowClick={handleRowClick}
                                    onAddColumn={handleAddColumn}
                                    onAcknowledgeNewColumns={layout.actions.acknowledgeNewColumns}
                                    onDragEnd={handleDragEnd}
                                    fixedHeader={props.infiniteScroll ? false : fixedHeader}
                                    collapsed={collapsed}
                                    rowHeight={rowHeight}
                                    onToggleGroup={props.infiniteScroll ? undefined : handleToggleGroup}
                                    onScrollEnd={handleScrollEnd}
                                    overscrollContain={!!props.infiniteScroll}
                                    selectable={!!props.selectable}
                                    onToggleRow={handleToggleRow}
                                    onToggleAll={handleToggleAll}
                                    isRowSelectable={props.isRowSelectable}
                                    getRowClassName={props.getRowClassName}
                                    getRowTooltip={props.getRowTooltip}
                                    infiniteScroll={props.infiniteScroll}
                                />
                            </motion.div>
                        )
                    ) : (
                        <motion.div
                            key={`mobile-${
                                fetchIsError
                                    ? "error"
                                    : actualData && actualData.length > 0
                                        ? "data"
                                        : fetchIsSuccess && fetchedData?.data?.length === 0
                                            ? "empty"
                                            : "loading"
                            }`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            style={{ height: "100%", width: "100%", overflow: "auto",paddingBottom:props.infiniteScroll ? 0 : "144px",  }}
                        >
                            {fetchIsError ? (
                                <ShGridError />
                            ) : actualData && actualData.length > 0 && phoneViewColumn ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                        px: 3,
                                        pt: 3,
                                        ...props.gridOptions?.styles?.["mobile-root"],
                                    }}
                                >
                                    {actualData.map((row, rowIdx) => {
                                        const params = {
                                            value: row[phoneViewColumn.field],
                                            row,
                                            field: phoneViewColumn.field,
                                            colDef: phoneViewColumn,
                                            rowIndex: rowIdx,
                                            isEditable: false,
                                            hasFocus: false,
                                            api: {},
                                            getValue: (f) => row[f],
                                            isSelected: selectedItems.includes(row.id),
                                        };
                                        return (
                                            <Box key={row.id ?? rowIdx}>
                                                {phoneViewColumn.renderCell(params)}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ) : fetchIsSuccess && fetchedData?.data?.length === 0 ? (
                                <ShGridEmpty />
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }} />
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            {/* -------------------- FLOATING PAGINATION BAR -------------------- */}
            {!props.infiniteScroll && actualData && actualData.length > 0 && totalPages > 0 && (
                <Box
                    className={cx("sh-grid-bottom-bar", {
                        sidepanel: props.isSidepanelGrid,
                        dark: theme.palette.mode === "dark",
                    })}
                    sx={{
                        background:
                            theme.palette.mode === "dark"
                                ? colors.grey[500]
                                : "rgba(255,255,255,.75)",
                        backdropFilter: "blur(8px)",
                        boxShadow: "0px 0px 32px rgba(49,65,95,.45)",
                        position: "fixed",
                        zIndex: 200000,
                        left: "calc(50% + 342px)",
                        transform: "translateX(calc(-50% - 171px))",
                        bottom: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        height: 56,
                        borderRadius: 7,
                        transition: "500ms",
                        gap: 0.5,
                        "&.sidepanel": {
                            left: "unset",
                            right: 468,
                            transform: "translateX(50%)",
                        },
                        "@media screen and (max-width: 85.5em)": {
                            left: "50%",
                            transform: "translateX(-50%)",
                        },
                        "@media screen and (max-width: 47.9375em)": {
                            display: "none",
                        },
                        ...props.paginationSx,
                    }}
                >
                    {/* Prev arrow — matches ShDataGrid2 line 1691-1732 */}
                    <Box
                        component="button"
                        type="button"
                        onClick={prevPage}
                        disabled={activePage <= 1}
                        sx={{
                            ml:1,
                            height: 40,
                            width: 40,
                            display: "flex",
                            alignItems: "center",
                            borderRadius: 12,
                            color: colors.txt.secondary,
                            position: "relative",
                            cursor: "pointer",
                            userSelect: "none",
                            transition: "250ms",
                            border: "none",
                            background: "transparent",
                            fontWeight: 500,
                            "& .prev_icon": {
                                position: "absolute",
                                left: 18,
                            },
                            "&:hover": {
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[300]
                                        : colors.grey[100],
                                transition: "250ms",
                            },
                            "&:focus": { background: "transparent" },
                            "&:disabled": { cursor: "default", opacity: 0.3 },
                            "@media screen and (max-width: 47.9375em)": {
                                width: 48,
                                height: 48,
                            },
                        }}
                    >
                        <ArrowBackIos className="prev_icon" fontSize="14" />
                    </Box>

                    {/* Numbered pages clump */}
                    {paginationPages.map(
                        (page) =>
                            page !== totalPages && (
                                <Box
                                    key={`paginationPage-${page}`}
                                    component="button"
                                    type="button"
                                    className={cx("", {
                                        active: parseInt(activePage, 10) === parseInt(page, 10),
                                    })}
                                    sx={pillBtnSx}
                                    onClick={() => selectPage(page)}
                                >
                                    {page}
                                </Box>
                            )
                    )}

                    {/* Next-to-clump page (same pattern as ShDataGrid2 line 1797-1851) */}
                    {activePage < totalPages - 1 && totalPages > 5 && paginationPages.length > 0 && (
                        <Box
                            component="button"
                            type="button"
                            sx={pillBtnSx}
                            onClick={() =>
                                selectPage(paginationPages[paginationPages.length - 1] + 1)
                            }
                        >
                            {paginationPages[paginationPages.length - 1] + 1}
                        </Box>
                    )}

                    {/* PaginationDropUp "..." for pages in between */}
                    {totalPages > 5 && (
                        <PaginationDropUp
                            amount={totalPages}
                            onChange={(selected) => selectPage(selected)}
                            selected={activePage}
                            noSelection="..."
                            useTranslation={false}
                            closeOnSelection
                            disallowDeselection
                            transformOrigin={{ vertical: "bottom" }}
                            anchorOrigin={{ vertical: "top" }}
                        >
                            <Box
                                sx={{
                                    height: 40,
                                    minWidth: 40,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 12,
                                    color: "#fff",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    border: "none",
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[300]
                                            : colors.txt.primary,
                                    px: 1,
                                    fontWeight: "bold",
                                    "&:hover": {
                                        background:
                                            theme.palette.mode === "dark"
                                                ? colors.grey[200]
                                                : "#000",
                                    },
                                    "@media screen and (max-width: 47.9375em)": {
                                        width: 48,
                                        height: 48,
                                    },
                                }}
                            >
                                ...
                            </Box>
                        </PaginationDropUp>
                    )}

                    {/* Last page — ALWAYS rendered (no `totalPages > 1` guard). This
                        is the only path that renders page `totalPages`, because the
                        numbered map above deliberately skips it (`page !== totalPages`).
                        With a guard, the single-page case (`totalPages === 1`) would
                        drop out of every branch and show no page button at all.
                        Matches datagrid2/ShDataGrid2.jsx:1915-1968 which is unconditional. */}
                    <Box
                        component="button"
                        type="button"
                        className={cx("", {
                            active: parseInt(activePage, 10) === parseInt(totalPages, 10),
                        })}
                        sx={pillBtnSx}
                        onClick={() => selectPage(totalPages)}
                    >
                        {totalPages}
                    </Box>

                    {/* Next arrow — matches ShDataGrid2 line 1970-2011 */}
                    <Box
                        component="button"
                        type="button"
                        onClick={nextPage}
                        disabled={activePage >= totalPages}
                        sx={{
                            height: 40,
                            width: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 12,
                            color: colors.txt.secondary,
                            position: "relative",
                            cursor: "pointer",
                            userSelect: "none",
                            transition: "250ms",
                            border: "none",
                            background: "transparent",
                            "& .next_icon": {
                                position: "absolute",
                                left: 12,
                            },
                            "&:hover": {
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[300]
                                        : colors.grey[100],
                                transition: "250ms",
                            },
                            "&:focus": { background: "transparent" },
                            "&:disabled": { cursor: "default", opacity: 0.3 },
                            "@media screen and (max-width: 47.9375em)": {
                                width: 48,
                                height: 48,
                            },
                        }}
                    >
                        <ArrowForwardIos className="next_icon" fontSize="14" />
                    </Box>

                    {/* Page size selector — DropdownBase pill matching ShDataGrid2 line 2012-2084 */}
                    <Box sx={{ ml: 2 }}>
                        <DropdownBase
                            key={pageSize}
                            options={shDatagridPageSizeOptions || []}
                            onChange={(selected) => handlePageSizeUpdate(selected)}
                            selected={shDatagridPageSizeOptions?.indexOf(pageSize) || 0}
                            noSelection={t("no_label")}
                            useTranslation
                            closeOnSelection
                            disallowDeselection
                            transformOrigin={{ vertical: "bottom" }}
                            anchorOrigin={{ vertical: "top" }}
                        >
                            <Box
                                className="active"
                                sx={{
                                    height: 40,
                                    minWidth: 40,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 12,
                                    color: colors.txt.secondary,
                                    position: "relative",
                                    cursor: "pointer",
                                    userSelect: "none",
                                    transition: "250ms",
                                    border: "none",
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[300]
                                            : colors.grey[100],
                                    pl: 1,
                                    pr: 1,
                                    mr:1,
                                    whiteSpace: "nowrap",
                                    "b": {
                                        color: colors.txt.primary,
                                    },
                                    "&:hover": {
                                        background: colors.grey[200],
                                        transition: "250ms",
                                    },
                                    "@media screen and (max-width: 47.9375em)": {
                                        width: 48,
                                        height: 48,
                                    },
                                }}
                            >
                                <b>{pageSize}</b>&nbsp;{t("per_page")}
                                {/* Total row count for the active filter set (API `totalItems`) */}
                                {totalItems > 0 && (
                                    <>
                                        <Box
                                            sx={{
                                                width: "1px",
                                                alignSelf: "stretch",
                                                flexShrink: 0,
                                                my: 1,
                                                mx: 1.5,
                                                background:
                                                    theme.palette.mode === "dark"
                                                        ? colors.grey[400]
                                                        : colors.grey[200],
                                            }}
                                        />
                                        <b>{totalItems}</b>&nbsp;{t("results")}
                                    </>
                                )}
                            </Box>
                        </DropdownBase>
                    </Box>

                    {props.selectedActions}
                </Box>
            )}

            {/* Mobile */}
            {!props.infiniteScroll && rows?.length > 0 &&
                <Box 
                    className={cx('',{
                        'sidepanel':props.isSidepanelGrid,
                    })}
                    sx={{
                        position:'fixed',
                        background:"rgba(255,255,255,.75)",
                        backdropFilter:"blur(8px)",
                        boxShadow:"0px 0px 32px rgba(49,65,95,.45)",
                        borderRadius:7,
                        zIndex:200000,
                        left:"calc(50% + 270px)",
                        transform:"translateX(calc(-50% - 135px))",
                        bottom:32,
                        px:1,
                        
                        display:"flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        height:56,
                        gap:1,
                        "@media screen and (max-width: 47.9375em)" : {
                            position:"fixed",
                            bottom:72,
                            // background:theme.palette.mode === "dark" ? colors.grey[100] : colors.bg["secondary"],
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            height:48,
                            // paddingRight:2,
                            left:"50%",
                            transform:"translateX(-50%)",
                            borderRadius:8,
                            boxShadow: `0px 0px 10px 0px ${theme.palette.mode === "dark" ? colors.txt["secondary"] : "rgba(49, 65, 95, 0.35)"}`
                        },
                        "@media screen and (min-width: 47.9375em)" : {
                            display:"none"
                        }
                    }}
                >
                    <Box
                    component={"button"}
                    type="button"
                        className="active"
                        sx={{
                            height:40,
                            width:40,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            borderRadius:12,
                            color: colors.txt["secondary"],
                            position: "relative",
                            cursor: "pointer",
                            userSelect:"none",
                            transition:"250ms",
                            border:"none",
                            background:"transparent",

                            "& .prev_icon" : {
                                position: "absolute",
                                left:20
                            },

                            "&:hover" : {
                                // background: colors.grey[50],
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                transition:"250ms",

                            },
                            "&:focus" : {
                                background:"transparent"
                            },
                            "@media screen and (max-width: 47.9375em)" : {
                                width:48,
                                height:48
                            }
                        }}
                        onClick={() => prevPage()}
                    >
                        <ArrowBackIos className="prev_icon" fontSize="14"/>
                    </Box>
                    <PaginationDropUp
                            // options={pages || []}
                            onChange={(selected) => setActivePage(selected)}
                            selected={activePage}
                            amount={totalPages + 1}
                            noSelection={t("no_label")} 
                            useTranslation 
                            closeOnSelection
                            disallowDeselection
                            // dropUp
                            transformOrigin={{
                                vertical: "bottom"
                            }}
                            anchorOrigin={{
                                vertical:"top"
                            }}
                        >
                            <Box
                                    className="active"
                                    sx={{
                                        height:40,
                                        minWidth:40,
                                        display:"flex",
                                        alignItems:"center",
                                        justifyContent:"center",
                                        borderRadius:12,
                                        color: "#fff",
                                        position: "relative",
                                        cursor: "pointer",
                                        userSelect:"none",
                                        transition:"250ms",
                                        border:"none",
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.txt.primary,
                                        pl:1,
                                        pr:1,
                                        fontWeight:"bold",
                                        
            
                                        "& .prev_icon" : {
                                            position: "absolute",
                                            left:12
                                        },
            
                                        "&:hover" : {
                                            background: theme.palette.mode === "dark" ? colors.grey[400] : "#000",
                                            transition:"250ms",
            
                                        },
                                        
            
                                    }}
                                    // onClick={() => nextPage()}
                                >
                            {activePage < 10 ? "0" + activePage : activePage} 
                            </Box> 
                    </PaginationDropUp>


                    <Box
                    component={"button"}
                    type="button"
                        className="active"
                        sx={{
                            height:40,
                            width:40,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"center",
                            borderRadius:12,
                            color: colors.txt["secondary"],
                            position: "relative",
                            cursor: "pointer",
                            userSelect:"none",
                            transition:"250ms",
                            border:"none",
                            background:"transparent",

                            "& .prev_icon" : {
                                position: "absolute",
                                left:12
                            },

                            "&:hover" : {
                                background: colors.grey[50],
                                transition:"250ms",

                            },
                            "&:focus" : {
                                background:"transparent"
                            },

                            "@media screen and (max-width: 47.9375em)" : {
                                width:48,
                                height:48
                            }
                        }}
                        onClick={() => nextPage()}
                    >
                        <ArrowForwardIos className="next_icon" fontSize="14"/>
                    </Box>
                </Box>
            }
            <Box
                sx={{
                    "@media screen and (min-width: 47.9375em)" : {
                        display:"none"
                    }
                }}
            >

                {props.selectedActions}
            </Box>

            <MSaveGridConfig
                open={saveAsNewOpen}
                handleClose={() => setSaveAsNewOpen(false)}
                onSave={handleSaveAsNew}
                defaultConfigId={layout.defaultConfigId}
                familyMembers={familyMembers}
                willUnlinkShared={!layout.isActiveConfigLocal && !!layout.activeConfig}
                sharedSourceName={
                    !layout.isActiveConfigLocal ? layout.activeConfig?.name : null
                }
            />
            <MSaveGridConfig
                open={updateViewOpen}
                handleClose={() => setUpdateViewOpen(false)}
                onSave={handleUpdateView}
                activeConfig={layout.activeConfig}
                activeConfigId={layout.activeConfigId}
                defaultConfigId={layout.defaultConfigId}
                familyMembers={familyMembers}
                willUnlinkShared={!layout.isActiveConfigLocal && !!layout.activeConfig}
                sharedSourceName={
                    !layout.isActiveConfigLocal ? layout.activeConfig?.name : null
                }
            />
        </Box>
    );
};

export default memo(ShDataGrid3);
